import React from 'react';
import { ContentRow, FooterColumnConfig, SocialLinkConfig } from '../../../types/homepage';

export interface BlockTheme {
    surfaceColor?: string;
    textColor?: string;
    mutedColor?: string;
    borderColor?: string;
    fontFamily?: string;
}

const DEFAULT_THEME: Required<BlockTheme> = {
    surfaceColor: '#1a2236',
    textColor: '#ffffff',
    mutedColor: '#9ca3af',
    borderColor: 'rgba(255,255,255,0.1)',
    fontFamily: "'DM Sans', sans-serif",
};

function useThemeCss(theme: BlockTheme | undefined, prefix: string): string {
    const t = { ...DEFAULT_THEME, ...theme };
    return `
    .${prefix} {
      --surface: ${t.surfaceColor};
      --text: ${t.textColor};
      --muted: ${t.mutedColor};
      --border: ${t.borderColor};
      --font: ${t.fontFamily};
      font-family: ${t.fontFamily};
    }
  `;
}

interface FooterBlockProps {
    row: ContentRow;
    theme?: BlockTheme;
    onNavClick?: (url: string) => void;
}

export const FooterBlock: React.FC<FooterBlockProps> = ({ row, theme, onNavClick }) => {
    const { settings } = row;
    const css = useThemeCss(theme, 'ftr-root');

    const logoText = (settings?.logoText as string) || '';
    const logoImage = (settings?.logoImage as string) || '';
    const logoUrl = (settings?.logoUrl as string) || '/';
    const copyrightText = (settings?.copyrightText as string) || `© ${new Date().getFullYear()} All rights reserved.`;
    const columns = (settings?.footerColumns as FooterColumnConfig[]) || [];
    const socialLinks = (settings?.socialLinks as SocialLinkConfig[]) || [];
    const socialTitle = (settings?.socialTitle as string) || 'Follow Us';
    const bgColor = (settings?.backgroundColor as string) || '#1a2236';
    const textColor = (settings?.textColor as string) || '#ffffff';
    const linkColor = (settings?.linkColor as string) || '#c9ced9';
    const accentColor = (settings?.socialIconColor as string) || '#3d4a63';

    const handleLinkClick = (e: React.MouseEvent, url: string) => {
        if (onNavClick) {
            e.preventDefault();
            onNavClick(url);
        }
    };

    return (
        <>
            <style>{`
                ${css}
                .ftr-root.ftr-section {
                    background: ${bgColor}; color: ${textColor};
                    padding: 40px 24px 28px;
                }
                .ftr-inner {
                    max-width: 1440px; margin: 0 auto;
                    display: flex; flex-wrap: wrap; gap: 40px;
                    justify-content: space-between;
                }
                .ftr-brand { flex: 1 1 260px; min-width: 220px; }
                .ftr-logo { display: flex; align-items: center; gap: 8px; text-decoration: none; margin-bottom: 16px; }
                .ftr-logo img { height: 28px; display: block; }
                .ftr-logo-text { font-weight: 800; font-size: 17px; color: ${textColor}; font-family: var(--font); }
                .ftr-copyright { font-size: 12.5px; color: var(--muted); margin: 0; line-height: 1.6; }
                .ftr-col { flex: 0 1 160px; min-width: 140px; }
                .ftr-col-title { font-size: 14px; font-weight: 700; color: ${textColor}; margin: 0 0 14px; font-family: var(--font); }
                .ftr-col-links { display: flex; flex-direction: column; gap: 10px; }
                .ftr-col-links a { font-size: 13px; color: ${linkColor}; text-decoration: none; }
                .ftr-col-links a:hover { color: ${textColor}; text-decoration: underline; }
                .ftr-social { flex: 0 1 200px; min-width: 180px; }
                .ftr-social-title { font-size: 14px; font-weight: 700; color: ${textColor}; margin: 0 0 14px; font-family: var(--font); }
                .ftr-social-row { display: flex; gap: 10px; flex-wrap: wrap; }
                .ftr-social-icon {
                    width: 34px; height: 34px; border-radius: 50%;
                    background: ${accentColor}; color: ${textColor};
                    display: flex; align-items: center; justify-content: center;
                    font-size: 13px; font-weight: 700; text-decoration: none;
                    transition: filter .15s, transform .15s;
                }
                .ftr-social-icon:hover { filter: brightness(1.25); transform: translateY(-2px); }
                @media (max-width: 640px) {
                    .ftr-inner { gap: 28px; }
                    .ftr-section { padding: 32px 16px 22px; }
                }
            `}</style>

            <footer className="ftr-root ftr-section">
                <div className="ftr-inner">
                    <div className="ftr-brand">
                        <a className="ftr-logo" href={logoUrl}>
                            {logoImage ? (
                                <img src={logoImage} alt={logoText || 'Logo'} />
                            ) : (
                                <span className="ftr-logo-text">{logoText || 'Logo'}</span>
                            )}
                        </a>
                        <p className="ftr-copyright">{copyrightText}</p>
                    </div>

                    {columns.map((col) => (
                        <div className="ftr-col" key={col.key}>
                            {col.title && <p className="ftr-col-title">{col.title}</p>}
                            <div className="ftr-col-links">
                                {col.links.map((l) => (
                                    <a key={l.key} href={l.url || '#'} onClick={(e) => handleLinkClick(e, l.url)}>
                                        {l.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}

                    {socialLinks.length > 0 && (
                        <div className="ftr-social">
                            <p className="ftr-social-title">{socialTitle}</p>
                            <div className="ftr-social-row">
                                {socialLinks.map((s) => (
                                    <a key={s.key} className="ftr-social-icon" href={s.url || '#'}
                                        target="_blank" rel="noopener noreferrer" aria-label={s.label}>
                                        {s.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </footer>
        </>
    );
};

export default FooterBlock;